import * as THREE from 'three';
import type { SoldierModel } from '../assets/modelFactories/SoldierFactory';

export type SoldierStats = {
  maxHealth: number;
  damage: number;
  fireRate: number;
  range: number;
  projectileSpeed: number;
};

export const SOLDIER_STATS: SoldierStats = {
  maxHealth: 20,
  damage: 4,
  fireRate: 1.2,
  range: 3.8,
  projectileSpeed: 18,
};

export type SoldierAttackEvent = {
  position: THREE.Vector3;
  targetPosition: THREE.Vector3;
};

export class Soldier {
  readonly group: THREE.Group;
  readonly muzzleSocket: THREE.Object3D;
  readonly stats = SOLDIER_STATS;
  health: number;
  active = true;
  cooldown = 0;

  private readonly temp = new THREE.Vector3();

  constructor(
    position: THREE.Vector3,
    rotationY: number,
    model: SoldierModel,
  ) {
    this.health = this.stats.maxHealth;
    this.group = model.root;
    this.muzzleSocket = model.muzzleSocket;
    this.group.position.copy(position);
    this.group.rotation.y = rotationY;
    this.group.userData.soldier = this;
  }

  update(delta: number, targetPosition: THREE.Vector3 | null): SoldierAttackEvent | null {
    if (!this.active) return null;

    this.cooldown = Math.max(0, this.cooldown - delta);

    if (targetPosition) {
      this.facePosition(targetPosition);
      if (this.cooldown <= 0) {
        this.cooldown = 1 / this.stats.fireRate;
        return { position: this.getMuzzlePosition(), targetPosition: targetPosition.clone() };
      }
    }

    return null;
  }

  getMuzzlePosition(): THREE.Vector3 {
    const pos = new THREE.Vector3();
    this.muzzleSocket.getWorldPosition(pos);
    return pos;
  }

  takeDamage(amount: number): void {
    if (!this.active) return;
    this.health -= amount;
    if (this.health <= 0) {
      this.active = false;
      this.group.visible = false;
    }
  }

  private facePosition(position: THREE.Vector3): void {
    this.temp.subVectors(position, this.group.position);
    this.temp.y = 0;
    if (this.temp.lengthSq() > 0.0001) {
      this.group.rotation.y = Math.atan2(this.temp.x, this.temp.z);
    }
  }

  dispose(): void {
    this.group.clear();
  }
}